import Vue from 'vue';
import { apolloClient } from '@/plugins/vue-apollo';
import {
  GET_TERMS_AND_CONDITIONS,
  ALERT_ACCEPTANCE_OF_TERMS_AND_CONDITIONS,
  areTermsAndConditionsRequired,
  areTyCRequired,
  GET_FORM_DATA,
  GET_TERMS_AND_CONDITION_BY_ID_OR_COUNTRY,
  GET_ACCEPTED_TERMS_CONDITIONS,
  GET_ORGANIZATION_ACCEPTED_LATEST_TERMS,
  SHOW_RED_GLOBE_NOTIFICATION,
} from '@/store/graphql/queries/termsAndConditions';
import {
  insertOrganizationAcceptedTerms,
  setRequireTermsAcceptanceFlagToFalse,
  insertOrganizationContactAndOrganization,
  setTermsAndConditionsStatusToTrue,
  setTyCStatusToTrue,
  termsAcceptedFromModal,
} from '@/store/graphql/mutations/termsAndConditions';
import { Notification as notify } from 'element-ui';
import { i18n } from '@/plugins/vue-i18n';
import router from '@/router';

const termsAndConditions = {
  namespaced: true,
  state: {
    termsAndConditions: {
      terms: null,
    },
    formData: {
      organization_contact: null,
      organization: null,
      user: null,
    },
    areTermsAndConditionsRequired: false,
    organization: {},
    user: {},
    acceptedTerms: [],
    orgAcceptedLatestTerms: {
      terms_and_condition: {
        terms: null,
      },
    },
    showRedGlobeNotification: false,
    termsURL: '/TermsAndConditions',
  },
  actions: {
    async getTermsAndConditions({ state }, variables) {
      try {
        const searchQuery = {
          query: GET_TERMS_AND_CONDITIONS,
          variables,
        };
        const result = await apolloClient.query(searchQuery);
        if (result && result.data) {
          state.termsAndConditions = result.data.terms_and_conditions[0];
          return result.data.terms_and_conditions[0];
        }
        return null;
      } catch (error) {
        window.console.error(error);
        return null;
      }
    },
    async alertAcceptanceTermsConditions() {
      try {
        const searchQuery = {
          query: ALERT_ACCEPTANCE_OF_TERMS_AND_CONDITIONS,
        };
        const result = await apolloClient.query(searchQuery);
        if (result.error || result.data.alertTermsAccepted.error) {
          throw new Error(result);
        }
      } catch (error) {
        window.console.error(error);
        notify.error({
          title: i18n.t('common.notification.error'),
          message: i18n.t('common.notification.errorMsg'),
        });
      }
    },
    async insertOrganizationAcceptedTerms(state, variables) {
      try {
        const mutationQuery = {
          mutation: insertOrganizationAcceptedTerms,
          variables,
        };
        const result = await apolloClient.mutate(mutationQuery);
        if (result.error) {
          throw new Error(result);
        }
      } catch (error) {
        window.console.error(error);
      }
    },
    async setRequireTermsAcceptanceFlagToFalse(state, variables) {
      try {
        const mutationQuery = {
          mutation: setRequireTermsAcceptanceFlagToFalse,
          variables,
        };
        const result = await apolloClient.mutate(mutationQuery);
        if (result.error) {
          throw new Error(result);
        }
      } catch (error) {
        window.console.error(error);
      }
    },
    // Diagram of the conditionals https://user-images.githubusercontent.com/41448429/172537793-02a2b2c3-8e0f-443a-832c-36ea3c7d1173.png
    async areTermsAndConditionsRequired({ state, commit, dispatch, rootState }, variables) {
      try {
        let result = null;
        const searchQuery = {
          query: areTermsAndConditionsRequired,
          variables,
        };
        if (rootState.auth.currentRole.toLowerCase() === 'mro_manage_supplier') result = await apolloClient.query(searchQuery);
        else result = await apolloClient.query({ query: areTyCRequired }); // Normal query for mro_supplier and supplier (both user's role don't have access to query the ID but has custom checks)
        if (!result || result.error) throw new Error(result);
        if (!(result.data && result.data.organization.length > 0 && result.data.user.length > 0)) {
          state.areTermsAndConditionsRequired = false;
          dispatch('auth/setLoadingDefaultPageToFalse', '', { root: true });
          return;
        }
        state.organization = result.data.organization[0];
        state.user = result.data.user[0];
        const redirectPath = window.localStorage.getItem('auth:redirect');
        if (router.currentRoute.fullPath === state.termsURL) return;
        else if (redirectPath && redirectPath === state.termsURL) {
          window.localStorage.removeItem('auth:redirect');
          router.push({
            path: state.termsURL,
          }).catch(() => {});
          return;
        }
        if (state.organization.newProvider) {
          if (rootState.auth.currentRole.toLowerCase() === 'mro_manage_supplier') {
            if (state.organization.requires_terms_acceptance) {
              if (state.user.contact_type.toLowerCase() === 'representante legal') {
                commit('REDIRECT_TO_PAGE_TERMS_AND_CONDITIONS');
                dispatch('dontShowModalTermsAndConditions');
                return;
              }
              commit('REDIRECT_TO_CONTACT_REP_LEGAL');
              return;
            }
            dispatch('dontShowModalTermsAndConditions');
            return;
          }
          commit('REDIRECT_TO_CONTACT_REP_LEGAL');
          return;
        }
        // Organization is old provider
        if (state.user.terms_and_conditions_viewed) {
          if (state.organization.requires_terms_acceptance) {
            // Shows modal terms and conditions
            state.areTermsAndConditionsRequired = true;
            return;
          }
          dispatch('dontShowModalTermsAndConditions');
          return;
        }
        // Shows modal terms and conditions
        state.areTermsAndConditionsRequired = true;
      } catch (error) {
        window.console.error(error);
        dispatch('dontShowModalTermsAndConditions');
      }
    },
    dontShowModalTermsAndConditions({ state, dispatch }) {
      state.areTermsAndConditionsRequired = false;
      dispatch('auth/setLoadingDefaultPageToFalse', '', { root: true });
    },
    async GET_FORM_DATA({ state }, variables) {
      try {
        const searchQuery = {
          query: GET_FORM_DATA,
          variables,
        };
        const result = await apolloClient.query(searchQuery);
        if (result.error) throw new Error(result);
        if (result.data) Vue.set(state, 'formData', result.data);
      } catch (error) {
        window.console.error(error);
      }
    },
    async GET_TERMS_AND_CONDITION_BY_ID_OR_COUNTRY(state, variables) {
      try {
        const searchQuery = {
          query: GET_TERMS_AND_CONDITION_BY_ID_OR_COUNTRY,
          variables,
        };
        const result = await apolloClient.query(searchQuery);
        if (result.error) throw new Error(result);
        return result.data.terms_and_conditions[0];
      } catch (error) {
        window.console.error(error);
        return null;
      }
    },
    async insertOrganizationContactAndOrganization(state, variables) {
      try {
        const mutationQuery = {
          mutation: insertOrganizationContactAndOrganization,
          variables,
        };
        const result = await apolloClient.mutate(mutationQuery);
        if (result.error) {
          throw new Error(result.error);
        }
        if (result.data) {
          return result.data.insert_organization_contact;
        }
        return null;
      } catch (error) {
        notify.error({
          title: i18n.t('common.notification.error'),
          message: i18n.t('common.notification.organizationContact'),
        });
        throw new Error(error);
      }
    },
    async setTermsAndConditionsStatusToTrue(state, variables) {
      try {
        const mutationQuery = {
          mutation: setTermsAndConditionsStatusToTrue,
          variables,
        };
        const result = await apolloClient.mutate(mutationQuery);
        if (result.error) {
          throw new Error(result.error);
        }
      } catch (error) {
        throw new Error(error);
      }
    },
    async setTyCStatusToTrue() {
      try {
        const result = await apolloClient.mutate({ mutation: setTyCStatusToTrue });
        if (result.error) {
          throw new Error(result.error);
        }
      } catch (error) {
        throw new Error(error);
      }
    },
    async termsAcceptedFromModal(state, variables) {
      try {
        const mutationQuery = {
          mutation: termsAcceptedFromModal,
          variables,
        };
        const result = await apolloClient.mutate(mutationQuery);
        if (result.error) {
          throw new Error(result.error);
        }
      } catch (error) {
        throw new Error(error);
      }
    },
    async getAcceptedTerms({ state }, variables) {
      try {
        const requestQuery = {
          query: GET_ACCEPTED_TERMS_CONDITIONS,
          variables,
        };
        const result = await apolloClient.query(requestQuery);
        if (result && result.data) {
          Vue.set(state, 'acceptedTerms', result.data.organization_accepted_terms);
        }
      } catch (err) {
        window.console.error(err);
      }
    },
    async getOrganizationAcceptedLatestTerms({ state }, variables) {
      try {
        const requestQuery = {
          query: GET_ORGANIZATION_ACCEPTED_LATEST_TERMS,
          variables,
        };
        const result = await apolloClient.query(requestQuery);
        if (result && result.data && result.data.organization_accepted_terms.length > 0) {
          Vue.set(state, 'orgAcceptedLatestTerms', result.data.organization_accepted_terms[0]);
          return result.data.organization_accepted_terms[0];
        }
        return {};
      } catch (err) {
        window.console.error(err);
        return {};
      }
    },
    async showRedGlobeNotification({ state }, variables) {
      try {
        const requestQuery = {
          query: SHOW_RED_GLOBE_NOTIFICATION,
          variables,
        };
        const result = await apolloClient.query(requestQuery);
        if (result && result.data && result.data.redGlobeNotification.length > 0) {
          Vue.set(state, 'showRedGlobeNotification', true);
          return result.data.redGlobeNotification[0];
        }
        return {};
      } catch (err) {
        window.console.error(err);
        return {};
      }
    },
  },
  mutations: {
    SET_TERMS_AND_CONDITIONS_REQUIRE_FLAG_FALSE(state) {
      state.areTermsAndConditionsRequired = false;
    },
    REDIRECT_TO_CONTACT_REP_LEGAL() {
      window.localStorage.removeItem('auth:redirect');
      if (router.currentRoute.fullPath === '/Contact-Rep-Legal') return;
      router.push({ name: 'terms-contact-rep-legal' }).catch(() => {});
    },
    REDIRECT_TO_PAGE_TERMS_AND_CONDITIONS(state) {
      state.areTermsAndConditionsRequired = true;
      window.localStorage.removeItem('auth:redirect');
      if (router.currentRoute.fullPath === state.termsURL || router.currentRoute.fullPath === '/Terms') return;
      router.push({ name: 'terms' }).catch(() => {});
    },
  },
};

export default termsAndConditions;



// WEBPACK FOOTER //
// ./src/store/modules/supplier/termsAndConditions.js